/**
 * Error Handler Utility for Vocalira
 * Converts technical errors to user-friendly messages in Portuguese
 */

// Error message mapping
const ERROR_MESSAGES = {
  // Network errors
  'Network Error': 'Sem conexão com a internet. Verifique sua conexão e tente novamente.',
  'timeout': 'A conexão demorou muito. Tente novamente.',
  'ECONNREFUSED': 'Servidor indisponível. Tente novamente em alguns instantes.',
  
  // HTTP Status errors
  400: 'Dados inválidos. Verifique as informações e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Recurso não encontrado.',
  429: 'Muitas tentativas. Aguarde um momento antes de tentar novamente.',
  500: 'Erro no servidor. Estamos trabalhando nisso.',
  502: 'Servidor temporariamente indisponível. Tente novamente.',
  503: 'Serviço em manutenção. Tente novamente em breve.',
  
  // Stripe errors
  'card_declined': 'Cartão recusado. Tente outro cartão.',
  'insufficient_funds': 'Saldo insuficiente no cartão.',
  'expired_card': 'Cartão expirado. Use outro cartão.',
  'invalid_card': 'Dados do cartão inválidos.',
  'processing_error': 'Erro no processamento. Tente novamente.',
  
  // Auth errors
  'invalid_credentials': 'Email ou senha incorretos.',
  'email_exists': 'Este email já está cadastrado.',
  'weak_password': 'Senha muito fraca. Use pelo menos 6 caracteres.',
  
  // Default
  'default': 'Algo deu errado. Tente novamente.'
};

/**
 * Get user-friendly error message
 * @param {Error|Object} error - Error object from API call
 * @returns {string} User-friendly message in Portuguese
 */
export const getErrorMessage = (error) => {
  // Network error
  if (error.message === 'Network Error' || !navigator.onLine) {
    return ERROR_MESSAGES['Network Error'];
  }
  
  // Timeout
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return ERROR_MESSAGES['timeout'];
  }
  
  // HTTP response error
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;
    
    // Check for specific error message from backend
    if (data?.detail) {
      // Don't expose internal messages
      if (typeof data.detail === 'string' && !data.detail.includes('Error') && !data.detail.includes('error')) {
        return data.detail;
      }
    }
    
    // Map status codes
    if (ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }
  }
  
  // Stripe specific errors
  if (error.type?.includes('stripe') || error.code?.startsWith('card_')) {
    return ERROR_MESSAGES[error.code] || ERROR_MESSAGES['processing_error'];
  }
  
  return ERROR_MESSAGES['default'];
};

/**
 * Check if error is retryable
 * @param {Error|Object} error 
 * @returns {boolean}
 */
export const isRetryableError = (error) => {
  const status = error.response?.status;
  const retryableCodes = [408, 429, 500, 502, 503, 504];
  
  return (
    error.message === 'Network Error' ||
    error.code === 'ECONNABORTED' ||
    retryableCodes.includes(status)
  );
};

/**
 * Log error for debugging (sanitized)
 */
export const logError = (context, error) => {
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, {
      message: error.message,
      status: error.response?.status,
      code: error.code
    });
  }
};

export default {
  getErrorMessage,
  isRetryableError,
  logError
};
